import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, X, ShoppingCart, Trash2, Tag, UserPlus, Search, User, AlertTriangle, Check } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { LineDiscountEditor } from '@/components/pos/LineDiscountEditor';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useCartStore } from '@/stores/cart.store';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useAuthStore } from '@/stores/auth.store';
import { usePromotionPreview } from '@/hooks/usePromotionPreview';
import { useLoyaltyRedeemPreview } from '@/hooks/useLoyaltyRedeemPreview';
import { useLocationStore } from '@/stores/location.store';
import { applyPromotionToTotal } from '@/lib/pos-total';
import { formatCurrency, formatMoney2 } from '@/lib/utils';
import { convertPkrToCurrency } from '@/lib/currency-convert';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { aggregateTaxBreakdown, formatTaxLabel } from '@/lib/tax';
import { notify } from '@/lib/toast';
import api from '@/lib/api';
import type { Customer } from '@/types/pos';

export function CartPanel({ onCheckout }: { onCheckout: () => void }) {
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('pos');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingQty, setEditingQty] = useState('');
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState('');
  const editPriceRef = useRef<HTMLInputElement>(null);
  const [recentIds, setRecentIds] = useState<Set<string>>(new Set());
  const [clearConfirm, setClearConfirm] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const customerSearchRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevItemIds = useRef<Set<string>>(new Set());

  const items = useCartStore((s) => s.items);
  const subtotal = useCartStore((s) => s.subtotal);
  const totalTax = useCartStore((s) => s.totalTax);
  const orderDiscountAmount = useCartStore((s) => s.orderDiscountAmount);
  const grandTotal = useCartStore((s) => s.grandTotal);
  const itemCount = useCartStore((s) => s.itemCount);
  const customer = useCartStore((s) => s.customer);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const clearCart = useCartStore((s) => s.clearCart);
  const setCustomer = useCartStore((s) => s.setCustomer);
  const setItemPrice = useCartStore((s) => s.setItemPrice);
  const setItemDiscount = useCartStore((s) => s.setItemDiscount);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingDiscountId, setEditingDiscountId] = useState<string | null>(null);
  const lineDiscountTotal = useCartStore((s) => s.lineDiscountTotal);
  const isOnlineForSettings = useOnlineStatus();
  // POS-I360: PKR stays the charged amount; the default currency is shown beside it when it is not PKR.
  const defaultCurrency = useDefaultCurrency();
  const secondary = (pkr: number) => (defaultCurrency ? formatMoney2(convertPkrToCurrency(pkr, defaultCurrency.exchangeRate), defaultCurrency.code) : null);
  // POS-I119: the manager PIN switch and threshold the order discount already honours also gate big line discounts.
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenant-settings-checkout'],
    queryFn: () =>
      api
        .get('/tenant/settings')
        .then((r) => r.data as { managerPinEnabled?: boolean; managerDiscountThreshold?: number }),
    staleTime: 60_000,
    enabled: isOnlineForSettings,
  });
  const managerPinEnabled = tenantSettings?.managerPinEnabled ?? false;
  const managerDiscountThreshold = tenantSettings?.managerDiscountThreshold ?? 20;
  const setOrderDiscount = useCartStore((s) => s.setOrderDiscount);
  const [orderDiscountOpen, setOrderDiscountOpen] = useState(false);
  const [orderDiscountInput, setOrderDiscountInput] = useState('');
  const commitOrderDiscount = useCallback(() => {
    const pct = parseFloat(orderDiscountInput);
    if (!isNaN(pct) && pct > 0) setOrderDiscount('PERCENTAGE', Math.min(pct, 100));
    setOrderDiscountOpen(false);
    setOrderDiscountInput('');
  }, [orderDiscountInput, setOrderDiscount]);
  const taxBreakdown = aggregateTaxBreakdown(items.map((i) => i.taxBreakdown));

  // Customer search
  const { data: customerResults = [] } = useQuery({
    queryKey: ['pos-customer-search', customerSearchQuery],
    queryFn: () =>
      api.get('/customers', { params: { search: customerSearchQuery, limit: 8 } })
        .then((r) => {
          const d = r.data;
          return (Array.isArray(d) ? d : d?.data ?? []) as Customer[];
        }),
    enabled: customerSearchOpen && customerSearchQuery.trim().length >= 1,
    staleTime: 10_000,
  });

  const queryClient = useQueryClient();
  const canCreateCustomer = hasPermission('customers:*') || hasPermission('customers:create') || hasPermission('customers:write');

  // Quick-create customer from the POS without leaving the cart (T-133).
  const createCustomerMutation = useMutation({
    mutationFn: (query: string) => {
      const trimmed = query.trim();
      const isPhone = /^[0-9+\-\s]{6,}$/.test(trimmed);
      const payload = isPhone
        ? { name: `Customer ${trimmed}`, phone: trimmed }
        : { name: trimmed };
      return api.post('/customers', payload).then((r) => r.data as Customer);
    },
    onSuccess: (created) => {
      setCustomer(created);
      setCustomerSearchOpen(false);
      setCustomerSearchQuery('');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      notify.created('Customer');
    },
    onError: (err) => notify.error(err, 'Could not create customer'),
  });

  // Focus the search input when opened
  useEffect(() => {
    if (customerSearchOpen) setTimeout(() => customerSearchRef.current?.focus(), 50);
  }, [customerSearchOpen]);

  // Promotions (active badges + live preview) come from one owner shared with the
  // CheckoutDialog so the Total shown here equals the Total the sale charges. (T-356)
  const { activePromotions, promotionPreview, promotionDiscount, previewFailed } = usePromotionPreview();

  // Promotion-aware total — mirrors the committed sale's promotion subtraction so
  // the displayed Total and the "Charge" button match what the sale will post. (T-356)
  // POS-I52: the server prices the cart (GST, ATL further tax for an unregistered buyer, order
  // discount, promotions) so Total and Charge match what Complete Sale will demand; the local sum
  // is only the fallback while the preview loads, fails or the till is offline.
  const { selectedLocationId } = useLocationStore();
  const { pricing: serverPricing } = useLoyaltyRedeemPreview({
    customerId: customer?.id,
    redeemPoints: 0,
    locationId: selectedLocationId,
  });
  const netTotal = serverPricing
    ? Math.round(serverPricing.totalAmount * 100) / 100
    : applyPromotionToTotal(grandTotal, promotionDiscount);
  const shownTaxBreakdown = serverPricing ? serverPricing.taxBreakdown : taxBreakdown;
  const shownTotalTax = serverPricing ? serverPricing.totalTax : totalTax;

  // Detect newly added items and highlight them briefly
  useEffect(() => {
    const currentIds = new Set(items.map((i) => i.id));
    const newIds = items.filter((i) => !prevItemIds.current.has(i.id)).map((i) => i.id);
    if (newIds.length > 0) {
      setRecentIds((prev) => new Set([...prev, ...newIds]));
      const t = setTimeout(() => {
        setRecentIds((prev) => {
          const next = new Set(prev);
          newIds.forEach((id) => next.delete(id));
          return next;
        });
      }, 900);
      return () => clearTimeout(t);
    }
    prevItemIds.current = currentIds;
  }, [items]);

  // Keep prevItemIds in sync
  useEffect(() => {
    prevItemIds.current = new Set(items.map((i) => i.id));
  });

  // Focus inline qty input when it appears
  useEffect(() => {
    if (editingId) editInputRef.current?.select();
  }, [editingId]);

  const startQtyEdit = useCallback((item: { id: string; quantity: number; product?: { serialTracking?: boolean } }) => {
    // Serial-tracked items are always qty=1 — do not allow inline editing
    if (item.product?.serialTracking) return;
    setEditingId(item.id);
    setEditingQty(String(item.quantity));
  }, []);

  const commitQtyEdit = useCallback(() => {
    if (!editingId) return;
    const qty = parseInt(editingQty, 10);
    if (!isNaN(qty) && qty > 0) updateQuantity(editingId, qty);
    else if (!isNaN(qty) && qty <= 0) removeItem(editingId);
    setEditingId(null);
  }, [editingId, editingQty, updateQuantity, removeItem]);

  const startPriceEdit = useCallback((item: { id: string; unitPrice: number }) => {
    setEditingPriceId(item.id);
    setEditingPrice(String(item.unitPrice));
    setTimeout(() => editPriceRef.current?.select(), 30);
  }, []);

  const commitPriceEdit = useCallback(() => {
    if (!editingPriceId) return;
    const price = parseFloat(editingPrice);
    if (!isNaN(price) && price >= 0) setItemPrice(editingPriceId, price);
    setEditingPriceId(null);
  }, [editingPriceId, editingPrice, setItemPrice]);

  // POS-I119: the discount editor (amount by default, percent on toggle) is its own component.
  const startDiscountEdit = useCallback((item: { id: string }) => {
    setEditingDiscountId(item.id);
  }, []);

  // Cart-line keyboard navigation: ↑/↓ select, +/- qty, Del remove, * discount
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (isInput) return;
      // Don't intercept while a modal dialog (checkout, manager approval, etc.) is open
      if (document.querySelector('.p-dialog, [role="dialog"]')) return;
      if (!items.length) return;

      const idx = items.findIndex((i) => i.id === selectedId);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = idx < 0 ? 0 : Math.min(idx + 1, items.length - 1);
        setSelectedId(items[next].id);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = idx < 0 ? items.length - 1 : Math.max(idx - 1, 0);
        setSelectedId(items[prev].id);
        return;
      }
      if (idx < 0) return;
      const item = items[idx];

      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        if (!item.product.serialTracking) updateQuantity(item.id, item.quantity + 1);
        return;
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        if (!item.product.serialTracking) updateQuantity(item.id, item.quantity - 1);
        return;
      }
      if (e.key === 'Delete') {
        e.preventDefault();
        const nextSel = items[idx + 1]?.id ?? items[idx - 1]?.id ?? null;
        removeItem(item.id);
        setSelectedId(nextSel);
        return;
      }
      if (e.key === '*') {
        e.preventDefault();
        startDiscountEdit(item);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items, selectedId, updateQuantity, removeItem, startDiscountEdit]);

  const clearHold = useCallback(() => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    if (holdInterval.current) clearInterval(holdInterval.current);
  }, []);

  const startHold = useCallback((itemId: string, delta: number) => {
    holdTimer.current = setTimeout(() => {
      holdInterval.current = setInterval(() => {
        const { items: cur, updateQuantity: upd, removeItem: rem } = useCartStore.getState();
        const item = cur.find((i) => i.id === itemId);
        if (!item) { clearHold(); return; }
        const next = item.quantity + delta;
        if (next <= 0) { rem(itemId); clearHold(); }
        else upd(itemId, next);
      }, 80);
    }, 450);
  }, [clearHold]);

  return (
    <div className="flex h-full flex-col border-s glass" data-cart-panel>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-primary-text shrink-0" />
          <div>
            <h2 className="text-base font-semibold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent leading-tight">
              {t('currentSale')}
            </h2>
            <p className="text-xs text-muted-foreground">
              {itemCount} {itemCount === 1 ? t('itemSingular') : t('itemPlural')}
            </p>
          </div>
        </div>
        {items.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setClearConfirm(true)}
            className="text-muted-foreground hover:text-destructive-text h-10 text-sm px-3"
          >
            <Trash2 className="me-1 h-4 w-4" />
            {t('clear', { ns: 'common' })}
          </Button>
        )}
      </div>

      {/* Customer */}
      {customer ? (
        <div className="flex items-center justify-between border-b px-4 py-2 bg-primary/5">
          <div className="flex items-center gap-2 min-w-0">
            <User className="h-3.5 w-3.5 text-primary-text shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{customer.name}</p>
              <div className="flex items-center gap-1.5">
                {customer.phone && <p className="text-xs text-muted-foreground">{customer.phone}</p>}
                {customer.priceTier && (
                  <span className="text-xs bg-success/10 text-success-text px-1.5 py-0.5 rounded font-medium">
                    {customer.priceTier.name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setCustomer(null)} aria-label={t('removeCustomer')} className="h-11 w-11 text-muted-foreground shrink-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : customerSearchOpen ? (
        /* Inline customer search */
        <div className="border-b px-3 py-2 bg-primary/5">
          <div className="relative">
            <Search className="absolute start-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              ref={customerSearchRef}
              value={customerSearchQuery}
              onChange={(e) => setCustomerSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setCustomerSearchOpen(false); setCustomerSearchQuery(''); } }}
              placeholder={t('searchCustomerPlaceholder')}
              className="w-full h-11 rounded-md border bg-background ps-7 pe-11 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button aria-label={t('closeCustomerSearch')} onClick={() => { setCustomerSearchOpen(false); setCustomerSearchQuery(''); }} className="absolute end-1 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
          {customerResults.length > 0 && (
            <div className="mt-1.5 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-lg">
              {customerResults.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setCustomer(c); setCustomerSearchOpen(false); setCustomerSearchQuery(''); }}
                  className="w-full text-start px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center gap-2"
                >
                  <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    {c.phone && <p className="text-xs text-muted-foreground">{c.phone}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {customerSearchQuery.trim().length >= 1 && customerResults.length === 0 && (
            <div className="mt-1.5">
              <p className="text-xs text-muted-foreground text-center py-1">{t('noCustomersFound')}</p>
              {canCreateCustomer && (
                <button
                  onClick={() => createCustomerMutation.mutate(customerSearchQuery)}
                  disabled={createCustomerMutation.isPending}
                  className="w-full flex items-center justify-center gap-1.5 min-h-[44px] rounded-md border border-dashed border-primary/30 px-3 py-2 text-sm font-medium text-primary-text hover:bg-primary/5 transition-colors disabled:opacity-50"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  {createCustomerMutation.isPending
                    ? t('saving')
                    : `${t('createCustomer')} "${customerSearchQuery.trim()}"`}
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setCustomerSearchOpen(true)}
          className="flex min-h-[44px] items-center gap-2 border-b px-4 py-2 text-sm text-muted-foreground hover:bg-accent/50 transition-colors w-full"
        >
          <UserPlus className="h-3.5 w-3.5" />
          {t('addCustomer')} <span className="ms-auto opacity-50">{t('optionalLabel')}</span>
        </button>
      )}

      {/* Cart items */}
      <div className="flex-1 overflow-auto">
        {items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground px-6 text-center">
            <ShoppingCart className="h-12 w-12 opacity-15" />
            <div>
              <p className="text-sm font-medium">{t('emptyCart')}</p>
              <p className="text-xs opacity-70 mt-0.5">{t('cartEmptySubtitle')}</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {items.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                className={`px-3 py-2.5 transition-colors duration-700 ${
                  selectedId === item.id
                    ? 'bg-primary/5 ring-1 ring-inset ring-primary'
                    : recentIds.has(item.id)
                    ? 'bg-primary/10'
                    : 'hover:bg-accent/30'
                }`}
              >
                {/* Top row: name + total */}
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold leading-tight line-clamp-2">
                      {item.product.name}
                      {item.variant && (
                        <span className="font-normal text-muted-foreground"> — {item.variant.name}</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {editingPriceId === item.id ? (
                        <input
                          ref={editPriceRef}
                          type="number"
                          value={editingPrice}
                          onChange={(e) => setEditingPrice(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commitPriceEdit(); }
                            if (e.key === 'Escape') setEditingPriceId(null);
                          }}
                          onBlur={commitPriceEdit}
                          className="w-24 h-10 text-sm text-center border-2 border-warning/30 rounded bg-background outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          min={0}
                          step="0.01"
                        />
                      ) : (
                        <button
                          onClick={() => startPriceEdit(item)}
                          className="inline-flex min-h-[44px] items-center hover:text-warning-text hover:underline transition-colors"
                          title={t('cartOverridePriceHint')}
                        >
                          {formatCurrency(item.unitPrice)}
                        </button>
                      )}
                      {item.taxBreakdown.length > 0 && ` + ${formatCurrency(item.taxAmount)} tax`}
                    </p>
                    {item.discountAmount > 0 && editingDiscountId !== item.id && (
                      <p className="text-xs text-success-text">
                        {t('lineDiscountOff', { amount: formatCurrency(item.discountAmount) })}
                        {item.discountType === 'PERCENTAGE' ? ` (${item.discountValue}%)` : ''}
                      </p>
                    )}
                    {editingDiscountId !== item.id && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); startDiscountEdit(item); }}
                        className="mt-0.5 inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-primary-text hover:underline"
                        aria-label={item.discountAmount > 0 ? t('lineDiscountEdit') : t('lineDiscountAdd')}
                      >
                        <Tag className="h-4 w-4" />
                        {item.discountAmount > 0 ? t('lineDiscountEdit') : t('lineDiscountAdd')}
                      </button>
                    )}
                    {editingDiscountId === item.id && (
                      <LineDiscountEditor
                        itemName={item.product.name}
                        grossAmount={item.unitPrice * item.quantity}
                        current={{
                          type: item.discountAmount > 0 ? item.discountType : null,
                          value: item.discountValue,
                          reason: item.discountReason,
                          approvedById: item.discountApprovedById,
                        }}
                        managerPinEnabled={managerPinEnabled}
                        managerDiscountThreshold={managerDiscountThreshold}
                        onApply={(next) => {
                          setItemDiscount(item.id, next.type, next.value, {
                            reason: next.reason,
                            approvedById: next.approvedById,
                          });
                          setEditingDiscountId(null);
                        }}
                        onRemove={() => { setItemDiscount(item.id, null, 0); setEditingDiscountId(null); }}
                        onCancel={() => setEditingDiscountId(null)}
                      />
                    )}
                  </div>
                  <div className="flex flex-col items-end shrink-0">
                    <p className="text-base font-bold">{formatCurrency(item.lineTotal)}</p>
                    {defaultCurrency && (
                      <p className="text-xs text-muted-foreground" data-testid="cart-line-secondary"><bdi dir="ltr">{secondary(item.lineTotal)}</bdi></p>
                    )}
                    <button
                      onClick={() => removeItem(item.id)}
                      className="inline-flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-destructive-text transition-colors rounded"
                      aria-label={t('removeItem')}
                      title={t('removeItem')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Quantity row */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {item.product.serialTracking ? (
                    // Serial-tracked items are always qty=1; show a read-only indicator
                    <>
                      <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background opacity-30 cursor-not-allowed select-none">
                        <Minus className="h-4 w-4" />
                      </span>
                      <span className="w-16 h-11 text-center text-base font-bold flex items-center justify-center rounded-lg border border-dashed border-primary/30 text-primary-text select-none">
                        1
                      </span>
                      <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background opacity-30 cursor-not-allowed select-none">
                        <Plus className="h-4 w-4" />
                      </span>
                      <span className="ms-1 text-xs text-primary-text font-medium">{t('serialTracked')}</span>
                      <span className="ms-auto text-xs text-muted-foreground/50 font-mono">
                        ={formatCurrency(item.lineTotal)}
                      </span>
                    </>
                  ) : (
                    <>
                  {/* Minus — hold to repeat */}
                  <button
                    onMouseDown={() => startHold(item.id, -1)}
                    onMouseUp={clearHold}
                    onMouseLeave={clearHold}
                    onTouchStart={(e) => { e.preventDefault(); startHold(item.id, -1); }}
                    onTouchEnd={clearHold}
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    aria-label={t('decreaseQty')}
                    className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background hover:bg-destructive/10 hover:border-destructive/30 hover:text-destructive-text active:scale-90 transition-all duration-100 select-none touch-none"
                  >
                    <Minus className="h-4 w-4" />
                  </button>

                  {/* Quantity: click to edit inline */}
                  {editingId === item.id ? (
                    <input
                      ref={editInputRef}
                      type="number"
                      value={editingQty}
                      onChange={(e) => setEditingQty(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitQtyEdit(); }
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      onBlur={commitQtyEdit}
                      className="w-16 h-11 text-center text-base font-bold border-2 border-primary rounded-lg bg-background outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      min={0}
                    />
                  ) : (
                    <button
                      onClick={() => startQtyEdit(item)}
                      className="w-16 h-11 text-center text-base font-bold rounded-lg hover:bg-primary/10 hover:text-primary-text border border-transparent hover:border-primary/30 transition-all duration-100 select-none"
                      title={t('cartEditQtyHint')}
                    >
                      {item.quantity}
                    </button>
                  )}

                  {/* Plus — hold to repeat */}
                  <button
                    onMouseDown={() => startHold(item.id, 1)}
                    onMouseUp={clearHold}
                    onMouseLeave={clearHold}
                    onTouchStart={(e) => { e.preventDefault(); startHold(item.id, 1); }}
                    onTouchEnd={clearHold}
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    aria-label={t('increaseQty')}
                    className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background hover:bg-success/10 hover:border-success/30 hover:text-success-text active:scale-90 transition-all duration-100 select-none touch-none"
                  >
                    <Plus className="h-4 w-4" />
                  </button>

                  <span className="ms-auto text-xs text-muted-foreground/50 font-mono">
                    ={formatCurrency(item.lineTotal)}
                  </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Promotions Banner — shows live discount estimates as cart builds */}
      {activePromotions.length > 0 && (
        <div className="border-t border-success/15 bg-success/5 px-3 py-2">
          <p className="text-xs font-semibold text-success-text uppercase tracking-wide mb-1 flex items-center gap-1">
            <Tag className="h-3 w-3" /> {t('activePromotions')}
          </p>
          <div className="space-y-0.5">
            {activePromotions.map((promo) => {
              const applied = promotionPreview.find((p) => p.promotionId === promo.id);
              return (
                <div key={promo.id} className="flex items-center justify-between text-xs">
                  <span className={applied ? 'text-success-text font-medium' : 'text-muted-foreground/60'}>
                    {applied ? <Check className="inline h-3 w-3 align-text-bottom" /> : '•'} {promo.name}
                    {!applied && promo.minOrderValue && Number(promo.minOrderValue) > 0
                      ? ` (min ${formatCurrency(Number(promo.minOrderValue))})`
                      : ''}
                  </span>
                  {applied && (
                    <span className="font-semibold text-success-text">
                      -{formatCurrency(applied.discountAmount)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Totals + Checkout */}
      {items.length > 0 && (
        <div className="border-t border-border/60">
          <div className="px-4 py-2.5 space-y-1 text-base">
            <div className="flex justify-between text-muted-foreground">
              <span>{t('subtotal')}</span>
              {/* POS-I119: the subtotal shows the list prices; the item discounts follow as their own row */}
              <span>{formatCurrency(subtotal + lineDiscountTotal)}</span>
            </div>
            {lineDiscountTotal > 0 && (
              <div className="flex justify-between text-success-text">
                <span>{t('itemDiscounts')}</span>
                <span>-{formatCurrency(lineDiscountTotal)}</span>
              </div>
            )}
            {orderDiscountAmount > 0 && (
              <div className="flex justify-between text-success-text">
                <span className="flex items-center gap-1.5">
                  {t('discount')}
                  <button
                    type="button"
                    onClick={() => { setOrderDiscount(null, 0); setOrderDiscountInput(''); }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-destructive-text"
                    aria-label={t('removeOrderDiscount')}
                    title={t('removeOrderDiscount')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
                <span>-{formatCurrency(orderDiscountAmount)}</span>
              </div>
            )}
            {orderDiscountAmount <= 0 && (
              <div className="flex items-center justify-between text-sm">
                {orderDiscountOpen ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      autoFocus
                      type="number"
                      min={0}
                      max={100}
                      step="1"
                      value={orderDiscountInput}
                      onChange={(e) => setOrderDiscountInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitOrderDiscount(); }
                        if (e.key === 'Escape') setOrderDiscountOpen(false);
                      }}
                      onBlur={commitOrderDiscount}
                      className="h-8 w-20"
                    />
                    <span className="text-xs text-muted-foreground">{t('orderDiscountPercentHint')}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setOrderDiscountOpen(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-text hover:underline"
                  >
                    <Tag className="h-4 w-4" /> {t('addOrderDiscount')}
                  </button>
                )}
              </div>
            )}
            {promotionPreview.length > 0 && (
              <div className="flex justify-between text-success-text text-sm">
                <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> {t('promotionsApplied')}</span>
                <span>-{formatCurrency(promotionPreview.reduce((s, p) => s + p.discountAmount, 0))}</span>
              </div>
            )}
            {/* Non-blocking warning when the promo preview errored — so a failed check
                is visible rather than silently showing the pre-discount total. (T-369) */}
            {previewFailed && (
              <div className="flex items-center gap-1 text-warning-text text-sm">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                <span>{t('promoPreviewFailedShort')}</span>
              </div>
            )}
            {shownTaxBreakdown.map((tax) => (
              <div key={`${tax.name}-${tax.rate}-${tax.type}`} className="flex justify-between text-sm text-muted-foreground/80">
                <span>{formatTaxLabel(tax)}</span>
                <span>{formatCurrency(tax.amount)}</span>
              </div>
            ))}
            {shownTotalTax > 0 && shownTaxBreakdown.length === 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>{t('tax')}</span>
                <span>{formatCurrency(shownTotalTax)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-border/60 pt-2 text-2xl font-bold">
              <span>{t('total')}</span>              <span className="bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                {formatCurrency(netTotal)}
              </span>
            </div>
            {defaultCurrency && (
              <div className="flex justify-end text-sm text-muted-foreground" data-testid="cart-total-secondary">
                <bdi dir="ltr">{secondary(netTotal)}</bdi>
              </div>
            )}
          </div>

          <div className="px-3 pb-3">
            <Button
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-primary via-primary/90 to-primary/80 hover:from-primary/90 hover:via-primary/80 hover:to-primary/70 text-primary-foreground shadow-lg shadow-primary/25 active:scale-[0.98] transition-transform"
              onClick={onCheckout}
            >
              {t('chargeButton', { amount: formatCurrency(netTotal) })}
            </Button>
          </div>
        </div>
      )}

      {/* Clear Cart Confirm */}
      <ConfirmDialog
        open={clearConfirm}
        title={t('clearCart')}
        message={t('clearCartConfirm', { defaultValue: 'Remove all items from the cart?' })}
        confirmLabel={t('clear', { ns: 'common' })}
        variant="danger"
        onConfirm={() => { clearCart(); setClearConfirm(false); }}
        onCancel={() => setClearConfirm(false)}
      />
    </div>
  );
}
